import {
  fetchEcgDataMap,
  fetchEventIndicators,
  STUDY_STATUS,
  addSineWaveToEcgData,
  convertBinaryToECGData,
  createChartData,
  createMinutelyHrChartData,
  downloadSelectedEcgData,
  filterHourlySummaryRanges,
  validateEcgDataMap,
} from '@/entities/study';
import { NoDataComponent } from '@/shared/ui/components';
import { ArchivedNoDataIcon, BiodirectNoDataIcon } from '@/shared/ui/assets';

import {
  zeroPad,
  auth,
  showMessageError,
  GETTEXT_ALL_EVENT_TYPE,
  gettextSymptoms,
  addTimezoneToMoment,
  adjustStudyTime,
  convertToMoment,
  dateToStringWithDailyTimeZone,
  timezoneToOffset,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import axios from 'axios';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Highcharts from 'highcharts/highstock';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { checkIcorDevice } from '@/entities/device';
import { ECGClient, ECGViewer } from '@/widgets/ecg-viewer';
import NotAvailableComponent from '../notAvailableComponent/notAvailableComponent';
import type { EcgViewerTabProps } from './ecgViewerTab.types';
import EcgOptionsComponent from './ecgOptionsComponent';
import EventTableComponent from './eventTableComponent';
import EcgViewerGuideline from './ecgViewerGuideline';
import './ecgViewerTab.scss';

dayjs.extend(utc);

/**
 * Renders a study's ECG viewer tab with day navigation, the hourly heart-rate chart, the ECG strip and the events table. Returns a not-available panel for iCOR devices and studies not yet started and an archived notice for archived studies; otherwise it loads the day's summaries and indicators, draws the Highcharts chart with event indicators and guides, and wires column and event selection to the strip.
 *
 * @param dataProp - Study record providing friendly id, start and stop, timezone, time offset, device, facility and archived flags.
 * @param isActive - Whether the tab is currently active, gating data loading.
 * @param hasLoginBanner - Whether a login banner is shown, adjusting container spacing.
 * @returns The ECG viewer tab element, or a not-available placeholder.
 */
const EcgViewerTab = ({ data: dataProp, isActive, hasLoginBanner }: EcgViewerTabProps) => {
  const chartRef = useRef<any>(null);
  const eventTableComponentRef = useRef<any>(null);
  const currentSelectedIndexRef = useRef<any>(null);
  const currentSelectedEpochRef = useRef<any>(null);
  const previousSelectedIndexRef = useRef<any>(null);
  const needReloadData = useRef(false);

  const [isUnavailableStrip, setIsUnavailableStrip] = useState(false);

  const [state, setState] = useMergeState<any>({
    isLoadedData: false,
    isShowEcgViewer: false,
    isCheckedEventIndicators: true,
    selectedDate: null,
    selectedPoint: null,
    selectedEvent: null,
    hrs: [],
    guides: [],
    missingPoints: [],
    hrIndexes: [],
    eventIndexes: [],
    filteredEventIndexes: [],
    eventIndicators: [],
    hrArray: [],
    ecgDataMap: {},
    configChart: {},
    ecgObject: {},
    selectedMinHr: 0,
    selectedMaxHr: 0,
    minHr: undefined,
    maxHr: undefined,
    hasNoData: false,
    isZooming: false,
  });

  /**
   * Selects a minute column on the heart-rate chart and drives the ECG strip to that time. Marks the clicked column with the clicked class, restores the class and colour of the previously selected point in both series, records the new index and epoch in refs, and stores selectedPoint while dropping dataSamples so the strip reloads.
   *
   * @param selectedHr - Heart-rate chart point whose index identifies the clicked column.
   * @returns Nothing.
   */
  const handleSelectColumn = (selectedHr: any) => {
    const selectedColumn = chartRef.current.series[2].data[selectedHr.index];

    currentSelectedIndexRef.current = selectedColumn.index;
    currentSelectedEpochRef.current = selectedColumn.category;

    selectedColumn.update({ className: '--chart-clicked' });

    if (!_.isNil(previousSelectedIndexRef.current)) {
      chartRef.current.series[1].data[previousSelectedIndexRef.current].update({
        className: '',
      });
      chartRef.current.series[2].data[previousSelectedIndexRef.current].update({
        className: '--hidden',
        color: '#00A3AD',
      });
    }

    previousSelectedIndexRef.current = selectedColumn.index;

    const clonedEcgObject = _.cloneDeep(state.ecgObject);
    delete clonedEcgObject.dataSamples;

    setIsUnavailableStrip(false);
    setState({
      ecgObject: clonedEcgObject,
      selectedEvent: null,
      selectedMinHr: 0,
      selectedMaxHr: 0,
      selectedPoint: {
        index: currentSelectedIndexRef.current,
        start: currentSelectedEpochRef.current,
      },
    });
  };

  /**
   * Focuses the ECG strip on an event clicked in the events table. Scrolls the window to the top, rounds the event start to the enclosing minute in study time to derive its chart index, zooms the chart out, repaints the previous point back to teal and the new one red in the indicator and heart-rate series, and stores the event with its index.
   *
   * @param selectedEvent - Event row clicked in the events table, carrying its start time.
   * @returns Nothing.
   */
  const onClickEvent = (selectedEvent: any) => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    const roundMoment =
      dayjs(selectedEvent.start).second() === 0
        ? adjustStudyTime({
            dateMoment: dayjs(selectedEvent.start).startOf('minute'),
            studyTimeOffset: dataProp.studyTimeOffset,
          })
        : adjustStudyTime({
            dateMoment: dayjs(selectedEvent.start).add(1, 'minute').startOf('minute'),
            studyTimeOffset: dataProp.studyTimeOffset,
          });

    const firstEpoch = dayjs(state.selectedDate).startOf('day').valueOf();
    currentSelectedIndexRef.current = roundMoment.diff(firstEpoch, 'minute');
    currentSelectedEpochRef.current = roundMoment.valueOf();

    // *: Reset zoom
    chartRef.current.zoomOut();

    if (!_.isNil(previousSelectedIndexRef.current)) {
      chartRef.current.series[1].data[previousSelectedIndexRef.current].update({
        className: '',
      });
      chartRef.current.series[2].data[previousSelectedIndexRef.current].update({
        className: '--hidden',
        color: '#00A3AD',
      });
    }

    chartRef.current.series[1].data[currentSelectedIndexRef.current].update({
      className: '--hidden',
    });
    chartRef.current.series[2].data[currentSelectedIndexRef.current].update({
      className: '--event-clicked',
      color: '#F23D42',
    });

    previousSelectedIndexRef.current = currentSelectedIndexRef.current;

    const clonedEcgObject = _.cloneDeep(state.ecgObject);
    delete clonedEcgObject.dataSamples;

    setIsUnavailableStrip(false);
    setState({
      ecgObject: clonedEcgObject,
      selectedMinHr: 0,
      selectedMaxHr: 0,
      selectedEvent: { ...selectedEvent, index: currentSelectedIndexRef.current },
      selectedPoint: null,
    });
  };

  /**
   * Switches the ECG viewer to another study day. Converts the picked date into the study timezone at start of day, clears HR range, point and event selection along with the cached ecgDataMap, and when data is present destroys the Highcharts instance and drops dataSamples from a cloned ecgObject before merging the new state.
   *
   * @param date - Date value chosen in the day picker, read as UTC.
   * @returns Nothing.
   */
  const handleChangeDate = (date: any) => {
    const selectedDate = addTimezoneToMoment({
      dateMoment: dayjs.utc(date),
      timezone: dataProp.timezone,
    })! // date comes from the day picker, so dayjs.utc(date) is always a valid moment
      .clone()
      .startOf('day');

    const setStateObject: any = {
      selectedMinHr: 0,
      selectedMaxHr: 0,
      selectedEvent: null,
      selectedPoint: null,
      isLoadedData: false,
      hasNoData: false,
      ecgDataMap: {},
      selectedDate,
    };

    if (!state.hasNoData) {
      chartRef.current.destroy();

      const clonedEcgObject = _.cloneDeep(state.ecgObject);
      delete clonedEcgObject.dataSamples;
      _.assign(setStateObject, { ecgObject: clonedEcgObject });
    }

    setIsUnavailableStrip(false);
    setState(setStateObject);
  };

  /**
   * Loads the selected day's hourly ECG summaries and event indicators for the viewer. Fetches both in parallel over a window padded by five minutes, sorts the summaries by start, validates them, downloads each hour's heart-rate file, substitutes arrays of -1 for missing hours, derives a rounded min/max range plus the chart series, and falls back to hasNoData when a data URL is missing or validation fails.
   *
   * @returns A promise resolving once chart state or the no-data flag is stored.
   */
  const fetchData = async (): Promise<any> => {
    try {
      const selectedDateMoment = state.selectedDate.clone();

      // *: Fetch pre and post 5 minutes to get ranges for first and last point
      const startSearchDateEcgDataMap = adjustStudyTime({
        dateMoment: selectedDateMoment
          .clone()
          .subtract(1, 'day')
          .endOf('day')
          .subtract(5, 'minute'),
        studyTimeOffset: dataProp.studyTimeOffset,
        shouldRevertToServerTime: true,
      }).toISOString();
      const stopSearchDateEcgDataMap = adjustStudyTime({
        dateMoment: selectedDateMoment.clone().add(1, 'day').startOf('day').add(5, 'minute'),
        studyTimeOffset: dataProp.studyTimeOffset,
        shouldRevertToServerTime: true,
      }).toISOString();

      const startSearchDateEventIndicators = adjustStudyTime({
        dateMoment: selectedDateMoment.clone().subtract(1, 'day').endOf('day'),
        studyTimeOffset: dataProp.studyTimeOffset,
        shouldRevertToServerTime: true,
      }).toISOString();
      const stopSearchDateEventIndicators = adjustStudyTime({
        dateMoment: selectedDateMoment.clone().add(1, 'day').startOf('day'),
        studyTimeOffset: dataProp.studyTimeOffset,
        shouldRevertToServerTime: true,
      }).toISOString();

      const [ecgDataMap, eventIndicators] = await Promise.all([
        fetchEcgDataMap({
          studyFid: dataProp.friendlyId,
          start: startSearchDateEcgDataMap,
          stop: stopSearchDateEcgDataMap,
        }),
        fetchEventIndicators(
          {
            studyFid: dataProp?.friendlyId,
            sortOrder: 'asc',
            startSearchDate: startSearchDateEventIndicators,
            stopSearchDate: stopSearchDateEventIndicators,
          },
          0,
        ),
      ]);

      // *: Sort data by start time
      if (ecgDataMap) {
        ecgDataMap.data = _.orderBy(ecgDataMap.data, 'start', 'asc');
      }

      const { querySignature, data, gain, channels, samplingFrequency } = ecgDataMap ?? {};

      const setStateObject = {
        isLoadedData: true,
      };

      // const foundMissingData = _.find(data, x => !x.hrUrl || !x.dataUrl);
      /**
       * The first hourly summary of the selected day that carries no ECG data URL. Found by scanning the fetched summaries for a falsy dataUrl, so a truthy result marks the day as lacking usable data.
       *
       * @returns The offending summary, or undefined when every hour has a data URL.
       */
      const foundMissingData = _.find(data, (x) => !x?.dataUrl);
      if (data?.length && !foundMissingData) {
        const startEpoch = adjustStudyTime({
          dateMoment: selectedDateMoment.clone().startOf('day'),
          studyTimeOffset: dataProp.studyTimeOffset,
          shouldRevertToServerTime: true,
        }).valueOf();
        const stopEpoch = adjustStudyTime({
          dateMoment: selectedDateMoment.clone().endOf('day'),
          studyTimeOffset: dataProp.studyTimeOffset,
          shouldRevertToServerTime: true,
        }).valueOf();
        // *: Filter HR data in date
        /**
         * Hourly ECG summary entries whose start falls inside the selected day. Filters the fetched ecgDataMap data against the day's start and stop epochs, both already converted to server time.
         *
         * @returns The hourly summaries belonging to the selected day.
         */
        const currentDateData = _.filter(
          data,
          (x) => dayjs(x?.start).valueOf() >= startEpoch && dayjs(x?.start).valueOf() <= stopEpoch,
        );
        const isValidCurrentDateData = validateEcgDataMap(currentDateData);
        if (isValidCurrentDateData) {
          const downloadedHrPromises: any = [];
          const missingHrIndexes: any = [];
          _.forEach(currentDateData, (x, index) => {
            if (x?.hrUrl) {
              downloadedHrPromises.push(axios.get(`${x.hrUrl}?${querySignature}`));
            } else {
              missingHrIndexes.push(index);
            }
          });

          _.assign(setStateObject, {
            ecgDataMap,
            ecgObject: {
              adcGain: gain,
              adcZero: 0,
              hiddenChannels: _.range(channels?.length ?? 0),
              isShowBeatMarkers: true,
              numChannels: channels?.length ?? 0,
              samplingRate: samplingFrequency,
              diagnosticLead: 2,
            },
            configChart: {
              isJumpOffset: true,
              isDisplayScrollbar: true,
              isEdit: true,
            },
            eventIndicators,
          });

          if (downloadedHrPromises.length) {
            const downloadedHrArrayResponse: any = await Promise.all(downloadedHrPromises);
            /**
             * Per-hour minutely heart-rate sample arrays built from the downloaded HR file responses. Reduces each response body to a string and splits it on commas, pushing an empty array when the body is empty.
             *
             * @returns Array of comma-split HR sample arrays, one per downloaded hour.
             */
            const downloadedHrArray = _.reduce(
              downloadedHrArrayResponse,
              (array, item) => {
                const formatItemData = String(item.data);
                if (formatItemData?.length > 0) {
                  array.push(formatItemData.split(','));
                } else {
                  array.push([]);
                }
                return array;
              },
              [] as string[][],
            );
            const negativeHrArray = new Array(60).fill('-1');
            _.forEach(missingHrIndexes, (x) => {
              downloadedHrArray.splice(x, 1, [...negativeHrArray]);
            });
            const hrArray = createMinutelyHrChartData({
              hourlySummaries: currentDateData,
              minutelyHrs: downloadedHrArray,
              start: selectedDateMoment.clone().startOf('day'),
              stop: selectedDateMoment.clone().endOf('day'),
              studyTimeOffset: dataProp.studyTimeOffset,
            });

            let minHr;
            let maxHr;
            const clonedHrArray = [...hrArray];
            _.remove(clonedHrArray, (x) => x < 0);
            if (clonedHrArray.length > 0) {
              const minHrTmp = Math.floor(_.min(clonedHrArray) / 10) * 10;
              const maxHrTmp = Math.ceil(_.max(clonedHrArray) / 10) * 10;
              if (minHrTmp === maxHrTmp) {
                // *: Create min/max HR range with interval is 40
                minHr = minHrTmp - 40 > 0 ? minHrTmp - 40 : 0;
                maxHr = maxHrTmp + 40;
              } else {
                minHr = minHrTmp;
                maxHr = maxHrTmp;
              }
            }

            const { hrs, guides, eventGuides, missingPoints, hrIndexes, eventIndexes } =
              createChartData({
                hrArray,
                eventIndicators,
                selectedDate: selectedDateMoment,
                maxHr,
                studyTimeOffset: dataProp.studyTimeOffset,
              });

            _.assign(setStateObject, {
              isShowEcgViewer: downloadedHrArray.length > 0,
              hrArray,
              minHr,
              maxHr,
              hrs,
              guides,
              eventGuides,
              missingPoints,
              hrIndexes,
              eventIndexes,
              hasNoData: false,
            });
          }
        } else {
          _.assign(setStateObject, { hasNoData: true });
        }
      } else {
        _.assign(setStateObject, { hasNoData: true });
      }

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch data: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({ isLoadedData: true, hasNoData: true });
    }
  };

  /**
   * Toggles the event indicators overlay on the hourly heart-rate chart. Shows or hides Highcharts series index 1 according to the flipped flag and stores that flag back into state.
   *
   * @returns Nothing.
   */
  const handleCheckEventIndicators = () => {
    const newIsCheckedEventIndicators = !state.isCheckedEventIndicators;
    if (newIsCheckedEventIndicators) {
      chartRef.current.series[1].show();
    } else {
      chartRef.current.series[1].hide();
    }
    setState({ isCheckedEventIndicators: newIsCheckedEventIndicators });
  };

  /**
   * Steps the ECG viewer one day forward or backward from the arrow controls. Clears HR range, point and event selection plus the cached ecgDataMap, destroys the chart and strips dataSamples from a cloned ecgObject when data is present, then sets selectedDate to the adjacent day.
   *
   * @param isRight - True to move to the next day, false for the previous day.
   * @returns Nothing.
   */
  const handleClickArrow = (isRight: any) => {
    const setStateObject: any = {
      selectedMinHr: 0,
      selectedMaxHr: 0,
      selectedEvent: null,
      selectedPoint: null,
      isLoadedData: false,
      ecgDataMap: {},
      hasNoData: false,
    };

    if (!state.hasNoData) {
      chartRef.current.destroy();

      const clonedEcgObject = _.cloneDeep(state.ecgObject);
      delete clonedEcgObject.dataSamples;
      _.assign(setStateObject, { ecgObject: clonedEcgObject });
    }

    const newDate = isRight
      ? state.selectedDate.clone().add(1, 'day')
      : state.selectedDate.clone().subtract(1, 'day');
    _.assign(setStateObject, { selectedDate: newDate });

    setIsUnavailableStrip(false);
    setState(setStateObject);
  };

  /**
   * Applies a heart-rate range selected on the chart to the viewer state. Writes the given bounds into selectedMinHr and selectedMaxHr.
   *
   * @param minHr - Lower heart-rate bound of the selection.
   * @param maxHr - Upper heart-rate bound of the selection.
   * @returns Nothing.
   */
  const updateMinMaxHr = ({ minHr, maxHr }: any) => {
    setState({
      selectedMinHr: minHr,
      selectedMaxHr: maxHr,
    });
  };

  /**
   * Downloads and assembles the five-minute ECG strip centred on the selected point or event. Builds a plus/minus 2.5 minute window reverted to server time for chart points, filters the hourly summary ranges, downloads and converts the binary samples, pads gaps through addSineWaveToEcgData, offsets eventPos to the middle sample plus five seconds for events, and flags the strip unavailable when nothing downloads.
   *
   * @param start - Start timestamp of the selected point or event to centre on.
   * @returns A promise resolving once the strip is stored or the failure is logged.
   */
  const prepareEcgData = async (start: any) => {
    try {
      const { studyTimeOffset } = dataProp;
      const { channels, samplingFrequency } = state.ecgDataMap;
      const startMoment = state.selectedPoint
        ? adjustStudyTime({
            dateMoment: dayjs(start),
            studyTimeOffset,
            shouldRevertToServerTime: true,
          }).subtract(2.5, 'minute')
        : dayjs(start).subtract(2.5, 'minute');
      const stopMoment = state.selectedPoint
        ? adjustStudyTime({
            dateMoment: dayjs(start),
            studyTimeOffset,
            shouldRevertToServerTime: true,
          }).add(2.5, 'minute')
        : dayjs(start).add(2.5, 'minute');
      const ranges = filterHourlySummaryRanges({
        summaries: state.ecgDataMap,
        start: startMoment,
        stop: stopMoment,
        channels,
        samplingFrequency,
        willAddDataRanges: true,
      });
      const downloadedSelectedEcgData = await downloadSelectedEcgData(
        ranges,
        state.ecgDataMap.querySignature,
      );

      if (downloadedSelectedEcgData.length === 0) {
        setIsUnavailableStrip(true);
        return;
      }

      const clonedEcgObject = _.cloneDeep(state.ecgObject);
      const selectedEcgData = convertBinaryToECGData(channels.length, downloadedSelectedEcgData);
      const {
        ecgData: addedSineWaveSelectedEcgData,
        missingSampleLengthPre,
        missingSampleLengthPost,
      } = addSineWaveToEcgData(selectedEcgData, ranges, startMoment, stopMoment, state.ecgDataMap);
      _.assign(clonedEcgObject, {
        dataSamples: addedSineWaveSelectedEcgData,
        missingSampleLengthPre,
        missingSampleLengthPost,
      });

      // *: Jump to middle of ECG when selecting event
      if (state.selectedEvent) {
        const middleSample = addedSineWaveSelectedEcgData[0].length / 2;

        //* Adding 5s to fit with event trigger time
        _.assign(clonedEcgObject, { eventPos: middleSample + 5 * 250 });
      } else {
        _.assign(clonedEcgObject, { eventPos: 0 });
      }

      setIsUnavailableStrip(false);
      setState({ ecgObject: clonedEcgObject });
    } catch (error) {
      console.error('Failed to download selected ECG data: ', error);
    }
  };

  /**
   * Reloads the current day's ECG summaries and events from the reload control. Raises the needReloadData ref so the load effect refetches, clears selection and the cached ecgDataMap, and destroys the chart while dropping dataSamples from a cloned ecgObject when data is present.
   *
   * @returns Nothing.
   */
  const onClickReload = () => {
    needReloadData.current = true;

    const setStateObject: any = {
      selectedMinHr: 0,
      selectedMaxHr: 0,
      selectedEvent: null,
      selectedPoint: null,
      isLoadedData: false,
      ecgDataMap: {},
      hasNoData: false,
    };

    if (!state.hasNoData) {
      chartRef.current.destroy();

      const clonedEcgObject = _.cloneDeep(state.ecgObject);
      delete clonedEcgObject.dataSamples;
      _.assign(setStateObject, { ecgObject: clonedEcgObject });
    }

    setIsUnavailableStrip(false);
    setState(setStateObject);
  };

  /**
   * Recolours the heart-rate chart's event indicators for the chosen event-type filter. Keeps every indicator for the all-types option and otherwise those whose type is not part of the selection, converts each event start into a minute index within the selected day, repaints that point translucent red, and stores the surviving indexes.
   *
   * @param eventType - Event type to filter by, or the all-event-types option.
   * @returns Nothing.
   */
  const updateEventType = (eventType: any): any => {
    const firstEpoch = state.selectedDate.clone().startOf('day').valueOf();
    /**
     * Event indicators retained for recoloring under the current event-type filter. Keeps every indicator when the all-event-types option is selected, otherwise only those whose type is not contained in the given event type.
     *
     * @returns The filtered list of event indicators.
     */
    const filteredEventIndicators = _.filter(state.eventIndicators, (x) =>
      eventType === GETTEXT_ALL_EVENT_TYPE ? true : !_.includes(eventType, x.type),
    );
    const filteredEventIndexes: any = [];
    _.forEach(filteredEventIndicators, (eventIndicator) => {
      const roundMoment =
        dayjs(eventIndicator.start).second() === 0
          ? adjustStudyTime({
              dateMoment: dayjs(eventIndicator.start).startOf('minute'),
              studyTimeOffset: dataProp.studyTimeOffset,
            })
          : adjustStudyTime({
              dateMoment: dayjs(eventIndicator.start).add(1, 'minute').startOf('minute'),
              studyTimeOffset: dataProp.studyTimeOffset,
            });
      const index = (roundMoment.valueOf() - firstEpoch) / 1000 / 60;
      if (index > -1) {
        filteredEventIndexes.push(index);
        chartRef.current.series[1].data[index].update({
          color: 'rgba(242, 61, 66, 0.6)', // *: With opacity 60%
        });
      }
    });
    setState({ filteredEventIndexes });
  };

  /**
   * Opens or closes the ECG viewer guideline drawer. Flips isVisibleGuideLineDrawer in component state.
   *
   * @returns Nothing.
   */
  const toggleGuideLineDrawer = () => {
    setState({
      isVisibleGuideLineDrawer: !state.isVisibleGuideLineDrawer,
    });
  };

  useEffect(() => {
    if (dataProp && dataProp.friendlyId) {
      if (dataProp.start) {
        const selectedDate = convertToMoment({
          isoDate: dataProp.start,
          timezone: dataProp.timezone,
          studyTimeOffset: dataProp.studyTimeOffset,
        });
        setState({ selectedDate });
      } else {
        setState({
          isLoadedData: true,
          hasNoData: true,
        });
      }
    }
  }, [dataProp.friendlyId]);

  useUpdateEffect(() => {
    if (state.selectedEvent || state.selectedPoint) {
      const { start } = state.selectedEvent || state.selectedPoint;
      prepareEcgData(start);
    }
  }, [state.selectedEvent, state.selectedPoint]);

  useUpdateEffect(() => {
    setState({ isZooming: false });
    if (needReloadData.current) {
      needReloadData.current = false;
      fetchData();
      eventTableComponentRef.current.handleReload();
    }
  }, [state.isLoadedData]);

  useUpdateEffect(() => {
    if (isActive && !dataProp.isArchived) {
      if (state.selectedDate && _.isEmpty(state.ecgDataMap)) {
        fetchData();
      }
    }
  }, [state.selectedDate, isActive]);

  useUpdateEffect((): any => {
    if (state.hrs.length && !state.hasNoData) {
      // *: Only draw chart when having HR data
      const hrsData: any[] = _.map(state.hrs, (d: any) => {
        const time = addTimezoneToMoment({
          startStudyDate: dataProp?.start,
          dateMoment: dayjs(d[0]),
          timezone: dataProp.timezone,
          facilityCountry: dataProp.facility?.contact?.country,
          facilityTimezone: dataProp.facility?.timezone,
        })!.format('HH:mm'); // d[0] is a valid chart epoch, so dayjs(d[0]) is always a valid moment
        const avgHr = d[1];
        return {
          time,
          x: d[0],
          y: avgHr,
          avgHr,
          tooltipText: !_.isNil(avgHr) && avgHr > -1 ? `${time} - ${avgHr} bpm` : `${time} - NA`,
          marker: {
            states: {
              hover: {
                enabled: false,
              },
            },
          },
          events: {
            click: (e: any) => {
              if (e.point.index !== currentSelectedIndexRef.current) {
                currentSelectedIndexRef.current = e.point.index;
                currentSelectedEpochRef.current = e.point.category;

                const columnPoint =
                  chartRef.current.series[2].data[currentSelectedIndexRef.current];
                columnPoint.update({ className: '--chart-clicked' });

                if (!_.isNil(previousSelectedIndexRef.current)) {
                  chartRef.current.series[1].data[previousSelectedIndexRef.current].update({
                    className: '',
                  });
                  chartRef.current.series[2].data[previousSelectedIndexRef.current].update({
                    className: '--hidden',
                    color: '#00A3AD',
                  });
                }

                previousSelectedIndexRef.current = e.point.index;

                const clonedEcgObject = _.cloneDeep(state.ecgObject);
                delete clonedEcgObject.dataSamples;

                setIsUnavailableStrip(false);
                setState({
                  ecgObject: clonedEcgObject,
                  selectedEvent: null,
                  selectedMinHr: 0,
                  selectedMaxHr: 0,
                  selectedPoint: {
                    index: currentSelectedIndexRef.current,
                    start: currentSelectedEpochRef.current,
                  },
                });
              }
            },
          },
        };
      });
      /**
       * Highcharts point objects for the hidden HR guide series. Maps each state.guides tuple to x and y and marks it with the hidden class.
       *
       * @returns Array of hidden guide points.
       */
      const guidesData = _.map(state.guides, (d) => ({
        x: d[0],
        y: d[1],
        className: '--hidden',
      }));
      /**
       * Highcharts point objects for the event guide series. Maps each state.eventGuides tuple to its x and y values.
       *
       * @returns Array of event guide points.
       */
      const eventGuidesData = _.map(state.eventGuides, (d) => ({
        x: d[0],
        y: d[1],
      }));
      /**
       * Highcharts point objects for the no-data area series. Maps each state.missingPoints tuple to x and y plus an HH:mm label produced by addTimezoneToMoment with the facility country and timezone, and a no-data tooltip text.
       *
       * @returns Array of missing-data points with labels and tooltips.
       */
      const missingPointsData = _.map(state.missingPoints, (d) => {
        const time = addTimezoneToMoment({
          startStudyDate: dataProp?.start,
          dateMoment: dayjs(d[0]),
          timezone: dataProp.timezone,
          facilityCountry: dataProp.facility?.contact?.country,
          facilityTimezone: dataProp.facility?.timezone,
        })!.format('HH:mm'); // d[0] is a valid chart epoch, so dayjs(d[0]) is always a valid moment
        return {
          time,
          x: d[0],
          y: d[1],
          tooltipText: `${time} - No data`,
        };
      });

      const negativeHrsZones: any[] = [];
      _.forEach(hrsData, (item, index) => {
        if (index === hrsData.length - 1) {
          // *: Draw until last point
          if (item.avgHr === -1 && hrsData[index - 1] && hrsData[index - 1]?.avgHr === -1) {
            negativeHrsZones.push({
              value: hrsData[index]?.x,
              dashStyle: 'dash',
            });
          }
        } else if (item.avgHr === -1) {
          if (
            hrsData[index - 1] &&
            (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
            hrsData[index + 1] &&
            (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
          ) {
            negativeHrsZones.push(
              { value: hrsData[index - 1]?.x },
              {
                value: hrsData[index + 1]?.x,
                dashStyle: 'dash',
              },
            );
          } else if (
            hrsData[index - 1] &&
            (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
            hrsData[index + 1] &&
            hrsData[index + 1]?.avgHr === -1
          ) {
            negativeHrsZones.push({ value: hrsData[index - 1]?.x });
          } else if (
            hrsData[index - 1] &&
            hrsData[index - 1]?.avgHr === -1 &&
            hrsData[index + 1] &&
            (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
          ) {
            negativeHrsZones.push({
              value: hrsData[index + 1]?.x,
              dashStyle: 'dash',
            });
          }
        }
      });

      // *: Re-assign y for dash HR point to draw dash line
      _.forEach(negativeHrsZones, (zone, index): any => {
        if (
          zone.dashStyle &&
          negativeHrsZones[index - 1] &&
          !negativeHrsZones[index - 1].dashStyle
        ) {
          /**
           * HR points falling inside a no-data zone that has to be drawn as a dashed line. Filters hrsData to the items between the previous zone boundary and this zone's value.
           *
           * @returns The HR points spanning the dashed zone.
           */
          const dashHrs = _.filter(
            hrsData,
            (item): any => item.x >= negativeHrsZones[index - 1].value && item.x <= zone.value,
          );
          const firstDashHrY = _.isNil(dashHrs[0].y) ? state.minHr : dashHrs[0].y;
          const lastDashHrY = _.isNil(dashHrs[dashHrs.length - 1].y)
            ? state.minHr
            : dashHrs[dashHrs.length - 1].y;
          const step = Math.abs((lastDashHrY - firstDashHrY) / dashHrs.length);
          /**
           * Interpolated y values used to draw the dashed line across a gap without HR data. Repeats a single value when both ends are equal, otherwise walks a range from the lower to the higher end by the computed step.
           *
           * @returns Array of y values, one per point of the dashed zone.
           */
          const incrementNumber =
            firstDashHrY === lastDashHrY
              ? new Array(dashHrs.length).fill(firstDashHrY)
              : firstDashHrY < lastDashHrY
                ? _.range(firstDashHrY, lastDashHrY, step)
                : _.range(lastDashHrY, firstDashHrY, step);
          _.forEach(dashHrs, (dashHr, dashIndex) => {
            /**
             * The HR chart point matching the current dash point's x value. Located in hrsData so its y can be replaced by the interpolated dash-line value.
             *
             * @returns The matching HR point, or undefined when none matches.
             */
            const foundHr = _.find(hrsData, (item) => item.x === dashHr.x);
            if (foundHr) {
              if (!_.isNil(foundHr.y)) {
                foundHr.y = incrementNumber[dashIndex];
              }
            }
          });
        }
      });

      // * Hide reset zoom button tooltip
      Highcharts.setOptions({
        lang: {
          resetZoomTitle: '',
        },
      });

      chartRef.current = Highcharts.stockChart('container', {
        chart: {
          width: 945,
          alignTicks: false,
          zoomType: 'x',
          selectionMarkerFill: 'rgba(34, 101, 198, 0.25)',
          resetZoomButton: {
            theme: {
              style: {
                display: 'none',
              },
            },
          },
          animation: false,
          spacingLeft: 1,
          spacingRight: 1,
          style: {
            fontFamily: 'Lato, Arial, Helvetica, sans-serif',
          },
          events: {
            load: (e: any) => {
              if (!state.selectedPoint && state.hrIndexes.length > 0) {
                const { series } = e.target;

                currentSelectedIndexRef.current = state.hrIndexes[0].index;
                currentSelectedEpochRef.current = state.hrIndexes[0].category;

                const columnPoint = series[2].data[currentSelectedIndexRef.current];
                columnPoint.update({
                  className: '--chart-clicked',
                  color: '#00A3AD',
                });

                previousSelectedIndexRef.current = state.hrIndexes[0].index;

                setState({
                  selectedPoint: {
                    index: currentSelectedIndexRef.current,
                    start: currentSelectedEpochRef.current,
                  },
                });
              }
            },
            selection: (e: any) => {
              const { resetSelection } = e;
              if (resetSelection) {
                setState({ isZooming: false });
                chartRef.current.update({ chart: { zoomType: 'x' } });
              } else {
                setState({ isZooming: true });
                chartRef.current.update({
                  chart: {
                    zoomType: undefined,
                    panning: {
                      enabled: false, // *: Scroll when zooming or not
                    },
                  },
                });
              }
            },
            click: (e: any) => {
              // *: Click to chart: nodeName === 'rect', click to Reset Zoom button: nodeName === 'tspan'
              if (e.target.nodeName !== 'tspan') {
                // *: Click to chart, not Reset Zoom button
                const clickedMoment = dayjs(e.xAxis[0].value);
                const seconds = clickedMoment.second();
                const roundedMoment =
                  seconds >= 30 ? clickedMoment.add(1, 'minute') : clickedMoment;
                const clickedCategory = addTimezoneToMoment({
                  startStudyDate: dataProp?.start,
                  dateMoment: roundedMoment.startOf('minute'),
                  timezone: dataProp.timezone,
                  facilityCountry: dataProp.facility?.contact?.country,
                  facilityTimezone: dataProp.facility?.timezone,
                })!.valueOf(); // roundedMoment is derived from a valid Highcharts axis value
                if (clickedCategory !== currentSelectedEpochRef.current) {
                  /**
                   * The HR series point sitting at the clicked category. Searched in the chart's HR series for an entry with a non-nil y and a matching category, so clicks on time without data find nothing.
                   *
                   * @returns The matching HR point, or undefined for a period without data.
                   */
                  const selectedHr = _.find(
                    chartRef.current.series[3].data,
                    (item) => !_.isNil(item.y) && item.category === clickedCategory,
                  );
                  if (selectedHr) {
                    handleSelectColumn(selectedHr);
                  }
                }
              }
            },
          },
        },
        series: [
          {
            type: 'area',
            data: missingPointsData,
            turboThreshold: missingPointsData.length, // *: Define number of data item are allowed
            color: 'rgb(75, 91, 115)',
            opacity: 0.08,
            marker: {
              enabled: false,
            },
            tooltip: {
              headerFormat: '',
              pointFormat: '{point.tooltipText}',
            },
            states: {
              hover: {
                enabled: false,
              },
            },
            dataGrouping: {
              enabled: false,
            },
            animation: {
              duration: 0,
            },
            grouping: false,
          },
          {
            type: 'column',
            data: eventGuidesData,
            turboThreshold: eventGuidesData.length, // *: Define number of data item are allowed
            color: '#F23D42',
            marker: {
              enabled: false,
            },
            tooltip: {
              pointFormatter: () => '',
              headerFormat: '',
              pointFormat: '',
            },
            states: {
              hover: {
                enabled: false,
              },
            },
            dataGrouping: {
              enabled: false,
            },
            animation: {
              duration: 0,
            },
            enableMouseTracking: false,
            clip: false,
            visible: state.isCheckedEventIndicators,
            grouping: false,
          },
          {
            type: 'column',
            data: guidesData,
            turboThreshold: guidesData.length, // *: Define number of data item are allowed
            color: '#00A3AD',
            marker: {
              enabled: false,
            },
            tooltip: {
              pointFormatter: () => '',
              headerFormat: '',
              pointFormat: '',
            },
            states: {
              hover: {
                enabled: false,
              },
            },
            dataGrouping: {
              enabled: false,
            },
            animation: {
              duration: 0,
            },
            enableMouseTracking: false,
            clip: false,
            grouping: false,
          },
          {
            type: 'spline',
            data: hrsData,
            color: '#0954BA',
            marker: {
              enabled: false,
            },
            zoneAxis: 'x',
            zones: negativeHrsZones,
            cursor: 'pointer',
            turboThreshold: hrsData.length, // *: Define number of data item are allowed
            tooltip: {
              headerFormat: '',
              pointFormat: '{point.tooltipText}',
            },
            states: {
              hover: {
                enabled: false,
              },
            },
            dataGrouping: {
              enabled: false,
            },
            animation: {
              duration: 0,
            },
            grouping: false,
          },
        ],
        xAxis: [
          {
            tickInterval: 3600 * 1000,
            tickLength: 0,
            gridLineWidth: 1,
            crosshair: {
              color: '#2265C6',
            },
            labels: {
              align: 'left',
              format: '{value:%H:%M}',
              step: 4,
            },
            minRange: 3600000,
            // *: Display scrollbar while zooming
            // scrollbar: {
            //   enabled: true
            // }
          },
        ],
        yAxis: [
          {
            title: {
              enabled: false,
            },
            opposite: true,
            min: state.minHr,
            max: state.maxHr,
            lineWidth: 1,
            tickLength: 0,
            tickWidth: 1,
            showLastLabel: true,
            labels: {
              align: 'left',
              x: 12,
              y: 3,
            },
            startOnTick: false, // *: Setting to display min/max
            stopOnTick: false, // *: Setting to display min/max
            tickPositioner: () => {
              // *: To determine displayed ticks
              const positions = [
                state.minHr,
                Math.ceil((state.maxHr + state.minHr) / 2),
                state.maxHr,
              ];
              return positions;
            },
          },
        ],
        title: {
          text: '',
        },
        tooltip: {
          animation: false,
          hideDelay: 0,
          useHTML: true,
          shared: false,
          split: false,
          backgroundColor: '#2265C6',
          borderColor: '#2265C6',
          borderRadius: 4,
          outside: true,
          positioner: (labelWidth: any, labelHeight: any, point: any) => {
            const rect = document.getElementById('container')?.getBoundingClientRect();
            return {
              x: (rect?.x ?? 0) + point.plotX - labelWidth / 2,
              y: (rect?.y ?? 0) - labelHeight - 10,
            };
          },
          shadow: false,
          style: {
            color: '#fff',
          },
          valueDecimals: 0,
          shape: 'rect',
        },
        plotOptions: {
          area: {
            states: {
              inactive: {
                enabled: false,
              },
            },
          },
          column: {
            pointWidth: 0.5,
            minPointLength: 62, // *: Make guides always have the same height of chart
            states: {
              inactive: {
                enabled: false,
              },
            },
          },
          spline: {
            states: {
              inactive: {
                enabled: false,
              },
            },
          },
        },
        time: {
          timezoneOffset: -1 * timezoneToOffset(dataProp.timezone),
        },
        credits: {
          enabled: false,
        },
        navigator: {
          enabled: false,
        },
        rangeSelector: {
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
      } as any);
    }
  }, [state.missingPoints, state.guides, state.eventGuides, state.eventIndexes, state.hrs]);

  /**
   * Start of the date range offered by the ECG viewer date picker. Memoized conversion of the study start ISO date into a moment with the study timezone and study time offset applied.
   *
   * @returns Moment marking the earliest selectable day.
   */
  const defaultStartDate = useMemo(
    () =>
      convertToMoment({
        isoDate: dataProp.start,
        timezone: dataProp.timezone,
        studyTimeOffset: dataProp.studyTimeOffset,
      }),
    [dataProp.start, dataProp.timezone, dataProp.studyTimeOffset],
  );

  /**
   * End of the date range offered by the ECG viewer date picker. Memoized: for completed or done studies, or once the study stop time has passed, it converts the study stop into a moment with timezone and study time offset applied, otherwise it converts the current time in the study timezone.
   *
   * @returns Moment marking the latest selectable day.
   */
  const defaultEndDate = useMemo(() => {
    const isEndOfDuration = dayjs().isAfter(dayjs(dataProp.stop));
    return _.includes([STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE], dataProp.status) ||
      isEndOfDuration
      ? convertToMoment({
          isoDate: dataProp.stop,
          timezone: dataProp.timezone,
          studyTimeOffset: dataProp.studyTimeOffset,
        })
      : convertToMoment({
          isoDate: dayjs().toISOString(),
          timezone: dataProp.timezone,
        });
  }, [dataProp.stop, dataProp.timezone, dataProp.studyTimeOffset]);

  const isIcorDevice = checkIcorDevice(dataProp.device);
  if (isIcorDevice) {
    return (
      <NotAvailableComponent
        containerClassName="ecg-viewer-tab"
        message={t`ECG viewer are not available for studies running on the iCOR device.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  const notStartedMessage =
    dataProp.isBiofluxDirect && !dataProp.start
      ? t`This study is running on Biodirect service and has not been started yet. Please hold until the study starts.`
      : !dataProp.start &&
        t`The study has not been started yet. Please hold until the study starts.`;
  if (notStartedMessage) {
    return (
      <NotAvailableComponent
        containerClassName="ecg-viewer-tab"
        message={notStartedMessage}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  if (dataProp.isArchived) {
    return (
      <div
        className={classNames('ecg-viewer-tab --is-archived animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <div className="archived-study-container">
          <img src={ArchivedNoDataIcon} alt="No data icon" />
          <div className="message">
            {t`The events in this study have been archived. If you need support to view ECG viewer and the events of this study, please contact the Biotricity customer support team.`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {state.isVisibleGuideLineDrawer && (
        <EcgViewerGuideline handleClickBack={toggleGuideLineDrawer} />
      )}

      <div
        className={classNames('ecg-viewer-tab', {
          '--hide': state.isVisibleGuideLineDrawer,
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <EcgOptionsComponent
          isCheckedEventIndicators={state.isCheckedEventIndicators}
          handleCheckEventIndicators={handleCheckEventIndicators}
          handleClickArrow={handleClickArrow}
          selectedDate={state.selectedDate}
          handleChangeDate={handleChangeDate}
          belowLimit={
            defaultStartDate
              ? dayjs(dayjs(defaultStartDate).format('MM/DD/YYYY')).startOf('day').valueOf()
              : undefined
          }
          aboveLimit={
            defaultEndDate
              ? dayjs(dayjs(defaultEndDate).format('MM/DD/YYYY')).endOf('day').valueOf()
              : undefined
          }
          isLoadedData={state.isLoadedData}
          onClickReload={onClickReload}
          hasNoData={state.hasNoData}
          toggleGuideLineDrawer={toggleGuideLineDrawer}
        />
        {/* // *: Zoom function */}
        {/* <button onClick={() => { chartRef.current.xAxis[0].setExtremes(100, 200); }}>Zoom in</button>
              <button onClick={() => { chartRef.current.zoomOut(); }}>Zoom out</button> */}

        {state.hasNoData ? (
          <div className="has-no-data-container">
            <NoDataComponent
              message={t`The ECG data of the selected day is currently not available.`}
            />
          </div>
        ) : (
          <>
            <div className="ecg-viewer-container">
              {state.isLoadedData ? (
                <div className="hr-chart-wrapper">
                  <div
                    id="container"
                    className="container"
                    style={{
                      width: '100%',
                      height: 107,
                      marginTop: 36,
                    }}
                  />
                  {state.isZooming && (
                    <span
                      className="reset-zoom"
                      aria-hidden="true"
                      onClick={() => {
                        chartRef.current.zoomOut();
                      }}
                    >
                      {t`Reset zoom`}
                    </span>
                  )}
                </div>
              ) : (
                <div className="hr-chart-skeleton-container">
                  <div className="chart-container">
                    <Skeleton height="100%" />
                  </div>
                </div>
              )}

              {(state.selectedEvent || state.selectedPoint) && (
                <div
                  className={classNames('event-info', state.selectedEvent ? '--event-clicked' : '')}
                >
                  {state.selectedEvent ? (
                    <>
                      <div className="left-container">
                        <div>{`Event ${zeroPad(state.selectedEvent.friendlyId)}`}</div>
                        <div>
                          {dateToStringWithDailyTimeZone({
                            startStudyDate: dataProp?.start,
                            isoDate: state.selectedEvent.start,
                            timezone: dataProp.timezone,
                            studyTimeOffset: dataProp.studyTimeOffset,
                            facility: dataProp.facility,
                            format: 'HH:mm:ss',
                          })}
                        </div>
                        <div>{`${state.selectedEvent.type} `}</div>
                      </div>
                      <div>
                        {state.selectedEvent.reason === 'None'
                          ? t`Not specified`
                          : gettextSymptoms(state.selectedEvent.reason) || ''}
                      </div>
                    </>
                  ) : state.selectedPoint ? (
                    <>
                      <div className="left-container">
                        {state.selectedPoint.index >= 3 && state.selectedPoint.index <= 1437 ? (
                          <>
                            <div>{`${state.selectedDate.format('MM/DD/YYYY')}`}</div>
                            <div>
                              {/* state.selectedPoint.start is a valid chart epoch, so dayjs(...) is always a valid moment */}
                              {`${addTimezoneToMoment({
                                startStudyDate: dataProp?.start,
                                dateMoment: dayjs(state.selectedPoint.start),
                                timezone: dataProp.timezone,
                                facilityCountry: dataProp.facility?.contact?.country,
                                facilityTimezone: dataProp.facility?.timezone,
                              })!
                                .subtract(2.5, 'minute')
                                .format('HH:mm:ss')} - ${addTimezoneToMoment({
                                startStudyDate: dataProp?.start,
                                dateMoment: dayjs(state.selectedPoint.start),
                                timezone: dataProp.timezone,
                                facilityCountry: dataProp.facility?.contact?.country,
                                facilityTimezone: dataProp.facility?.timezone,
                              })!
                                .add(2.5, 'minute')
                                .format('HH:mm:ss')} `}
                            </div>
                          </>
                        ) : (
                          <div>
                            {/* state.selectedPoint.start is a valid chart epoch, so dayjs(...) is always a valid moment */}
                            {`${addTimezoneToMoment({
                              startStudyDate: dataProp?.start,
                              dateMoment: dayjs(state.selectedPoint.start),
                              timezone: dataProp.timezone,
                              facilityCountry: dataProp.facility?.contact?.country,
                              facilityTimezone: dataProp.facility?.timezone,
                            })!
                              .subtract(2.5, 'minute')
                              .format('MM/DD/YYYY, HH:mm:ss')} - ${addTimezoneToMoment({
                              startStudyDate: dataProp?.start,
                              dateMoment: dayjs(state.selectedPoint.start),
                              timezone: dataProp.timezone,
                              facilityCountry: dataProp.facility?.contact?.country,
                              facilityTimezone: dataProp.facility?.timezone,
                            })!
                              .add(2.5, 'minute')
                              .format('MM/DD/YYYY, HH:mm:ss')} `}
                          </div>
                        )}
                      </div>
                      {state.selectedMinHr && state.selectedMaxHr ? (
                        <div>
                          {`${state.selectedMinHr || ''} - ${state.selectedMaxHr || ''} bpm`}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}

              {state.isShowEcgViewer &&
                (state.isLoadedData && !!state.ecgObject.dataSamples?.length ? (
                  <ECGClient>
                    <ECGViewer
                      isLoadedData={state.isLoadedData}
                      ecg={state.ecgObject}
                      configChart={state.configChart}
                      updateMinMaxHr={updateMinMaxHr}
                      timeObject={{
                        startMoment: dayjs(
                          state.selectedEvent?.start || state.selectedPoint?.start,
                        ).subtract(2.5, 'minute'),
                        timezone: dataProp.timezone,
                        // HR chart epochs already carry studyTimeOffset; only a raw event start still needs it
                        studyTimeOffset: state.selectedEvent ? dataProp.studyTimeOffset : 0,
                      }}
                      startStudyDate={dataProp?.start}
                      facilityCountry={dataProp.facility?.contact?.country}
                      facilityTimezone={dataProp.facility?.timezone}
                    />
                  </ECGClient>
                ) : isUnavailableStrip ? (
                  <div className="unavailable-strip-container">
                    <ReactSVG src={BiodirectNoDataIcon} />
                    <span className="message">
                      {t`The strip is currently unavailable. Please wait until it has been uploaded.`}
                    </span>
                  </div>
                ) : (
                  <div className="ecg-viewer-skeleton-container">
                    <div className="chart-container">
                      <Skeleton height="100%" />
                    </div>
                    <div className="scroller-container">
                      <Skeleton height="100%" />
                    </div>
                    <div className="menu-container">
                      <Skeleton height="100%" />
                    </div>
                  </div>
                ))}
            </div>

            <EventTableComponent
              ref={eventTableComponentRef}
              isLoadedDataProps={state.isLoadedData}
              studyFid={dataProp?.friendlyId}
              facility={dataProp.facility}
              timezone={dataProp?.timezone}
              studyTimeOffset={dataProp?.studyTimeOffset}
              selectedDate={state.selectedDate}
              eventIndicators={state.eventIndicators}
              onClickEvent={onClickEvent}
              updateEventType={updateEventType}
              selectedEvent={state.selectedEvent}
              startStudyDate={dataProp?.start}
            />
          </>
        )}
      </div>
    </>
  );
};

export default EcgViewerTab;
